import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  loadChatPreferences,
  updateChatPreference,
} from '../thunks/chatPreferencesThunks';

/**
 * Spec F (chat-proactive-greeting), Phase 10 — Redux state for the in-chat
 * settings popover (Phase 11) and the proactive greeting trigger (Phase 12).
 *
 * Holds two boolean preferences read/written through the existing Spec C
 * user-memory API under the `chat.preferences.*` namespace (strict-bucket
 * tenant isolation, handled server-side):
 *
 * - `chat.preferences.proactiveGreetingEnabled`     (default true)
 * - `chat.preferences.closeChatPanelOnNavigation`   (default true)
 *
 * Per Requirement 14.5, missing memory rows fall back to `true` so the UI
 * never has to explicitly seed defaults — `loadChatPreferences` resolves with
 * `true` for any key the backend doesn't return.
 */
export interface ChatPreferencesState {
  proactiveGreetingEnabled: boolean;
  closeChatPanelOnNavigation: boolean;
  isLoaded: boolean;
  isLoading: boolean;
  error: string | null;
}

const initialState: ChatPreferencesState = {
  proactiveGreetingEnabled: true,
  closeChatPanelOnNavigation: true,
  isLoaded: false,
  isLoading: false,
  error: null,
};

const chatPreferencesSlice = createSlice({
  name: 'chatPreferences',
  initialState,
  reducers: {
    /**
     * Synchronous local update — used by `updateChatPreference` for an
     * optimistic UI swap before the backend round-trip resolves.
     */
    setProactiveGreetingEnabled(state, action: PayloadAction<boolean>) {
      state.proactiveGreetingEnabled = action.payload;
    },
    setCloseChatPanelOnNavigation(state, action: PayloadAction<boolean>) {
      state.closeChatPanelOnNavigation = action.payload;
    },
    /**
     * Reset to defaults + `isLoaded = false`. Used on logout / account
     * identity change so the next chat session re-fetches against the new
     * tenant bucket.
     */
    resetChatPreferences(state) {
      state.proactiveGreetingEnabled = true;
      state.closeChatPanelOnNavigation = true;
      state.isLoaded = false;
      state.isLoading = false;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadChatPreferences.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(loadChatPreferences.fulfilled, (state, action) => {
        state.proactiveGreetingEnabled = action.payload.proactiveGreetingEnabled;
        state.closeChatPanelOnNavigation = action.payload.closeChatPanelOnNavigation;
        state.isLoaded = true;
        state.isLoading = false;
        state.error = null;
      })
      .addCase(loadChatPreferences.rejected, (state, action) => {
        // Still mark loaded so the popover doesn't retry forever on a
        // persistent backend error — defaults (already set in initial
        // state) remain in effect per Requirement 14.5.
        state.isLoaded = true;
        state.isLoading = false;
        state.error =
          (action.payload as string | undefined) ??
          action.error.message ??
          'Failed to load chat preferences';
      })
      .addCase(updateChatPreference.pending, (state) => {
        // Do NOT reset `isLoaded` — the popover should keep showing the
        // optimistic value while saving.
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateChatPreference.fulfilled, (state) => {
        // Optimistic setter already applied the new value; nothing else
        // to do here.
        state.isLoading = false;
      })
      .addCase(updateChatPreference.rejected, (state, action) => {
        // Rollback is performed inside the thunk (it dispatches the
        // synchronous setter again with the previous value). Surface the
        // error here.
        state.isLoading = false;
        state.error =
          (action.payload as string | undefined) ??
          action.error.message ??
          'Failed to save chat preference';
      });
  },
});

export const {
  setProactiveGreetingEnabled,
  setCloseChatPanelOnNavigation,
  resetChatPreferences,
} = chatPreferencesSlice.actions;

export default chatPreferencesSlice.reducer;
